import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, RefreshCw, Sparkles, X } from 'lucide-react';
import {
  acceptAiTransform,
  getAiTransformSet,
  getLatestAiTransformSet,
  regenerateAiTransformJob,
  rejectAiTransform,
  resolveImageUrl,
  startAiTransformSet,
} from '../lib/api';
import {
  AiTransformJob,
  AiTransformSet,
  AiTransformationStatus,
  POSE_VARIANTS,
  PoseVariant,
} from '../types';

interface Props {
  /**
   * URL of the original (mannequin) photo to feed into Gemini. Required:
   * the modal won't open with an empty source.
   */
  originalImageUrl: string;

  /** Closes the modal without applying any change. */
  onClose: () => void;

  /**
   * Called when the admin clicks "Terminer" with at least one kept pose.
   * The accepted transformed URLs are passed back in display order so the
   * parent form can append them to the product's image gallery.
   */
  onAccept: (transformedUrls: string[]) => void;
}

// Poll cadence: 3.5 s per tick. With 4 jobs running ~10-20 s each, expect
// the set to fully terminalize within ~6-10 ticks under normal latency.
const POLL_INTERVAL_MS = 3500;
// Generous ceiling to survive Gemini cold-starts on all 4 jobs in series
// (worst case ~120 s per job × small concurrency starvation = ~3 min).
const MAX_POLL_ATTEMPTS = 80;

function isTerminal(status: AiTransformationStatus): boolean {
  return (
    status === 'COMPLETED' ||
    status === 'FAILED' ||
    status === 'ACCEPTED' ||
    status === 'REJECTED'
  );
}

function setIsFullyTerminal(set: AiTransformSet): boolean {
  return POSE_VARIANTS.every((pose) => {
    const j = set.currentJobByPose[pose];
    return j !== undefined && isTerminal(j.status);
  });
}

/**
 * Modal that takes a mannequin photo URL, asks the backend to run it
 * through Gemini 2.5 Flash Image 4 times (one per pose variant), and
 * offers the admin a 2×2 grid where each pose can be individually:
 *   - kept ✓  (transformed URL queued for the product gallery)
 *   - rejected ✗  (server-side REJECTED, card grayed out)
 *   - regenerated ↻  (server-side: previous attempt marked REJECTED,
 *                     a fresh PENDING job appears for the same pose)
 *
 * Cost: 1 set = $0.039 × 4 = $0.156. Regeneration adds $0.039 per
 * rejected pose. The button copy surfaces the cost via tooltip.
 *
 * Click "Terminer" → onAccept receives the array of kept URLs in
 * display order (STANDING_FRONT → SEATED → BACK → WALKING).
 */
export function AiPhotoTransformModal({ originalImageUrl, onClose, onAccept }: Props) {
  const { t } = useTranslation();
  const [set, setSet] = useState<AiTransformSet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Job ids the admin has explicitly clicked "Keep" on. We don't accept
  // server-side until "Terminer" — keeps the flow snappy and lets the
  // admin toggle keep/unkeep without a backend round-trip per click.
  const [keptJobIds, setKeptJobIds] = useState<Set<string>>(new Set());
  // Per-job prompt edits, keyed by job id. Held here rather than in PoseCard
  // so a poll that refreshes the set doesn't wipe what the admin is typing.
  const [promptDrafts, setPromptDrafts] = useState<Record<string, string>>({});
  const [resuming, setResuming] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  // Resume the last shoot for this photo, so reopening the modal on a product
  // brings its poses back — each still individually regenerable — instead of
  // forcing four fresh generations to redo one image. Costs nothing: it reads
  // an existing set, it does not start one.
  useEffect(() => {
    let cancelled = false;
    if (!originalImageUrl) return;
    setResuming(true);
    getLatestAiTransformSet(originalImageUrl)
      .then((previous) => {
        if (cancelled || !previous) return;
        setSet(previous);
        // Poses already accepted are on the product — show them as kept so
        // finishing again doesn't quietly drop them.
        setKeptJobIds(
          new Set(
            previous.jobs
              .filter((j) => j.status === 'ACCEPTED')
              .map((j) => j.id),
          ),
        );
        if (!setIsFullyTerminal(previous)) {
          pollAttemptsRef.current = 0;
          pollTimerRef.current = window.setTimeout(
            () => pollSet(previous.id),
            POLL_INTERVAL_MS,
          );
        }
      })
      // A resume that fails must not block a fresh generation — the button
      // below still works, so this stays silent rather than showing an error.
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResuming(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalImageUrl]);

  function clearPollTimer() {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  async function pollSet(setId: string) {
    if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setError(t('adminProducts.ai.errors.pollTimeout'));
      return;
    }
    pollAttemptsRef.current += 1;
    try {
      const next = await getAiTransformSet(setId);
      setSet(next);
      if (!setIsFullyTerminal(next)) {
        pollTimerRef.current = window.setTimeout(
          () => pollSet(setId),
          POLL_INTERVAL_MS,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleStart() {
    setError(null);
    setSubmitting(true);
    pollAttemptsRef.current = 0;
    setKeptJobIds(new Set());
    try {
      const created = await startAiTransformSet({ originalImageUrl });
      setSet(created);
      if (!setIsFullyTerminal(created)) {
        pollTimerRef.current = window.setTimeout(
          () => pollSet(created.id),
          POLL_INTERVAL_MS,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function toggleKept(jobId: string) {
    setKeptJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }

  async function handleRejectPose(job: AiTransformJob) {
    setSubmitting(true);
    try {
      await rejectAiTransform(job.id);
      // Drop from kept set if it was queued.
      setKeptJobIds((prev) => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
      // Refresh set so the card flips to REJECTED visually.
      if (set) {
        const refreshed = await getAiTransformSet(set.id);
        setSet(refreshed);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegeneratePose(job: AiTransformJob, promptOverride?: string) {
    if (!set) return;
    setSubmitting(true);
    try {
      await regenerateAiTransformJob(job.id, promptOverride);
      // The edited prompt lives on the fresh job the server just created, so
      // drop the local draft and let the reloaded set be the source of truth.
      setPromptDrafts((prev) => {
        const next = { ...prev };
        delete next[job.id];
        return next;
      });
      // Drop the old job id from kept (the URL is about to be replaced).
      setKeptJobIds((prev) => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
      const refreshed = await getAiTransformSet(set.id);
      setSet(refreshed);
      pollAttemptsRef.current = 0;
      if (!setIsFullyTerminal(refreshed)) {
        clearPollTimer();
        pollTimerRef.current = window.setTimeout(
          () => pollSet(set.id),
          POLL_INTERVAL_MS,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Confirms the kept selection. Fires `accept` server-side for each
   * kept job (audit trail / status flip), then resolves the parent
   * callback with the kept URLs in pose display order. Server-side
   * accept is best-effort: any failure is logged but doesn't block the
   * parent callback (the image bytes are already on disk and serve OK).
   */
  async function handleFinish() {
    if (!set) return;
    if (keptJobIds.size === 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const keptInOrder: string[] = [];
      for (const pose of POSE_VARIANTS) {
        const job = set.currentJobByPose[pose];
        if (job && keptJobIds.has(job.id) && job.transformedImageUrl) {
          keptInOrder.push(job.transformedImageUrl);
          try {
            await acceptAiTransform(job.id);
          } catch (err) {
            // Don't block on accept-side failure; the URL is already
            // valid. Log via console and move on.
            console.warn('AI accept failed for', job.id, err);
          }
        }
      }
      onAccept(keptInOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const hasSet = set !== null;
  const keptCount = keptJobIds.size;

  // Stable per-pose card data, in display order.
  const cards = useMemo(() => {
    return POSE_VARIANTS.map((pose) => ({
      pose,
      job: set?.currentJobByPose[pose],
    }));
  }, [set]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-modal-title"
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        {/* Sticky: the pose grid is long, and the source thumbnail has to
            stay in view while a pose is being judged or its prompt edited —
            that comparison is the whole point of the screen. */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-200 bg-white">
          <h2
            id="ai-modal-title"
            className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2"
          >
            <Sparkles className="w-5 h-5 text-brass-dark" aria-hidden="true" />
            {t('adminProducts.ai.title')}
          </h2>
          <img
            src={resolveImageUrl(originalImageUrl)}
            alt={t('adminProducts.ai.originalLabel')}
            title={t('adminProducts.ai.originalLabel')}
            className="ms-auto me-2 h-12 w-12 shrink-0 rounded object-cover border border-gray-200 bg-gray-50"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('adminProducts.ai.close')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            {t('adminProducts.ai.introSet')}
          </p>

          {/* Source row */}
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-start">
            <figure className="space-y-2">
              <figcaption className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminProducts.ai.originalLabel')}
              </figcaption>
              <img
                src={resolveImageUrl(originalImageUrl)}
                alt={t('adminProducts.ai.originalLabel')}
                className="w-full aspect-[3/4] object-cover rounded-md border border-gray-200 bg-gray-50"
              />
            </figure>
            <div className="text-sm text-gray-700 space-y-2">
              <p>{t('adminProducts.ai.setHint1')}</p>
              <p>{t('adminProducts.ai.setHint2')}</p>
              {hasSet && (
                <p className="text-xs text-gray-500">
                  {t('adminProducts.ai.keptCount', { count: keptCount })}
                </p>
              )}
            </div>
          </div>

          {/* 2×2 grid of pose cards */}
          {hasSet && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {cards.map(({ pose, job }) => (
                <PoseCard
                  key={pose}
                  pose={pose}
                  job={job}
                  kept={job ? keptJobIds.has(job.id) : false}
                  submitting={submitting}
                  onToggleKept={toggleKept}
                  onReject={handleRejectPose}
                  onRegenerate={handleRegeneratePose}
                  promptDraft={job ? promptDrafts[job.id] : undefined}
                  onPromptDraftChange={(jobId, value) =>
                    setPromptDrafts((prev) => ({ ...prev, [jobId]: value }))
                  }
                />
              ))}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            {resuming && !hasSet && (
              <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('adminProducts.ai.resuming')}
              </span>
            )}

            {!hasSet && !submitting && !resuming && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t('adminProducts.ai.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleStart}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-brass-dark text-white hover:opacity-90"
                >
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  {t('adminProducts.ai.startSetCta')}
                </button>
              </>
            )}

            {submitting && (
              <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('adminProducts.ai.working')}
              </span>
            )}

            {hasSet && !submitting && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t('adminProducts.ai.cancel')}
                </button>
                {/* Still reachable with a set on screen: resuming must not
                    take away the option of starting the shoot over. */}
                <button
                  type="button"
                  onClick={handleStart}
                  title={t('adminProducts.ai.startSetCostHint')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5"
                >
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  {t('adminProducts.ai.startNewSet')}
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={keptCount === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-brass-dark text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" aria-hidden="true" />
                  {t('adminProducts.ai.finishCta', { count: keptCount })}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PoseCard — one cell in the 2×2 grid.
// Encapsulates per-pose state + action buttons. Lifted out for clarity;
// no useState of its own (parent owns the kept set + submit lock).
// -----------------------------------------------------------------------------

interface PoseCardProps {
  pose: PoseVariant;
  job: AiTransformJob | undefined;
  kept: boolean;
  submitting: boolean;
  onToggleKept: (jobId: string) => void;
  onReject: (job: AiTransformJob) => void | Promise<void>;
  onRegenerate: (job: AiTransformJob, promptOverride?: string) => void | Promise<void>;
  /** Draft prompt for this pose, or undefined when untouched. */
  promptDraft: string | undefined;
  onPromptDraftChange: (jobId: string, value: string) => void;
}

function PoseCard({
  pose,
  job,
  kept,
  submitting,
  onToggleKept,
  onReject,
  onRegenerate,
  promptDraft,
  onPromptDraftChange,
}: PoseCardProps) {
  const { t } = useTranslation();
  const [editingPrompt, setEditingPrompt] = useState(false);
  const status = job?.status;
  const isProcessing = status === 'PENDING' || status === 'PROCESSING';
  const isReady = status === 'COMPLETED' || status === 'ACCEPTED';
  const isFailed = status === 'FAILED';
  const isRejected = status === 'REJECTED';

  const poseLabel = t(`adminProducts.ai.poses.${pose}`);

  // Frame color hints at state: kept = brass, rejected = grayed out,
  // failed = red, otherwise neutral.
  const frameClasses = kept
    ? 'border-brass-dark ring-2 ring-brass-dark/40'
    : isFailed
      ? 'border-red-300'
      : isRejected
        ? 'border-gray-200 opacity-60'
        : 'border-gray-200';

  return (
    <figure
      className={`rounded-md border ${frameClasses} bg-white p-3 space-y-2 transition`}
      aria-label={poseLabel}
    >
      <figcaption className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
          {poseLabel}
        </span>
        {kept && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-brass-dark">
            <Check className="w-3 h-3" aria-hidden="true" />
            {t('adminProducts.ai.keptBadge')}
          </span>
        )}
      </figcaption>

      <div className="aspect-[3/4] w-full rounded-md bg-gray-50 overflow-hidden flex items-center justify-center">
        {isReady && job?.transformedImageUrl ? (
          <img
            src={resolveImageUrl(job.transformedImageUrl)}
            alt={poseLabel}
            className="w-full h-full object-cover"
          />
        ) : isProcessing ? (
          <div className="flex flex-col items-center text-brass-dark">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="mt-2 text-xs">{t('adminProducts.ai.processing')}</span>
          </div>
        ) : isFailed ? (
          <div className="px-3 text-center text-xs text-red-700">
            <div className="font-medium">{t('adminProducts.ai.failed')}</div>
            {job?.errorMessage && (
              // Scrollable + selectable rather than clamped: the provider's
              // diagnostic is the only clue when a generation comes back
              // empty, and a clamped one can't be read or copied.
              <div
                dir="ltr"
                className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-start text-[10px] text-red-600 select-text"
              >
                {job.errorMessage}
              </div>
            )}
          </div>
        ) : isRejected ? (
          <div className="text-xs text-gray-500">{t('adminProducts.ai.rejected')}</div>
        ) : (
          <div className="text-xs text-gray-400">{t('adminProducts.ai.placeholder')}</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        {isReady && job ? (
          <>
            <button
              type="button"
              onClick={() => onToggleKept(job.id)}
              disabled={submitting}
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md disabled:opacity-50 ${
                kept
                  ? 'bg-brass-dark text-white hover:opacity-90'
                  : 'border border-brass-dark text-brass-dark hover:bg-brass-dark/5'
              }`}
            >
              <Check className="w-3 h-3" aria-hidden="true" />
              {kept ? t('adminProducts.ai.kept') : t('adminProducts.ai.keep')}
            </button>
            <button
              type="button"
              onClick={() => onReject(job)}
              disabled={submitting}
              aria-label={t('adminProducts.ai.reject')}
              className="px-2 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => onRegenerate(job)}
              disabled={submitting}
              aria-label={t('adminProducts.ai.regenerate')}
              title={t('adminProducts.ai.regenerateCostHint')}
              className="px-2 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className="w-3 h-3" aria-hidden="true" />
            </button>
          </>
        ) : (isFailed || isRejected) && job ? (
          <button
            type="button"
            onClick={() => onRegenerate(job)}
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5 disabled:opacity-50"
          >
            <RefreshCw className="w-3 h-3" aria-hidden="true" />
            {t('adminProducts.ai.regenerate')}
          </button>
        ) : null}
      </div>

      {/* Per-pose prompt. Editing and rerunning ONE pose costs a single quota
          unit and leaves the other three untouched — relaunching the set to
          fix one image would spend four and discard three good results. */}
      {job && !isProcessing && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setEditingPrompt((v) => !v)}
            aria-expanded={editingPrompt}
            className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
          >
            <Pencil className="w-3 h-3" aria-hidden="true" />
            {editingPrompt
              ? t('adminProducts.ai.promptHide')
              : t('adminProducts.ai.promptEdit')}
          </button>

          {editingPrompt && (
            <div className="mt-1.5 space-y-1.5">
              <label className="sr-only" htmlFor={`pose-prompt-${job.id}`}>
                {t('adminProducts.ai.promptEdit')}
              </label>
              <textarea
                id={`pose-prompt-${job.id}`}
                dir="ltr"
                rows={6}
                value={promptDraft ?? job.prompt ?? ''}
                onChange={(e) => onPromptDraftChange(job.id, e.target.value)}
                className="w-full rounded-md border border-gray-300 text-gray-900 px-2 py-1.5 text-[11px] font-mono leading-relaxed text-start focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onRegenerate(job, promptDraft ?? job.prompt)}
                  disabled={submitting}
                  title={t('adminProducts.ai.regenerateCostHint')}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-brass-dark text-brass-dark hover:bg-brass-dark/5 disabled:opacity-50"
                >
                  <RefreshCw className="w-3 h-3" aria-hidden="true" />
                  {t('adminProducts.ai.promptRegenerate')}
                </button>
                {promptDraft !== undefined && promptDraft !== job.prompt && (
                  <button
                    type="button"
                    onClick={() => onPromptDraftChange(job.id, job.prompt ?? '')}
                    className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                  >
                    {t('adminProducts.ai.promptReset')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}
